import React, { useEffect, useState } from "react";
import CdrFilters from "./CdrFilters";
import CdrTable from "./CdrTable";
import CdrPagination from "./CdrPagination";
import CdrService from "../../services/v2/cdr-service";
import PermissionsKo from "../shared/PermissionsKo";

const CdrMain = () => {
  const [pitId, setPitId] = useState(null);
  const [cdrData, setCdrData] = useState([]);

  const [filters, setFilters] = useState({});

  const [pagination, setPagination] = useState({
    page: 1,
    size: 15,
    total: 0,
    sortBy: "initDate",
    sortOrder: "desc",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const [rowHeight, setRowHeight] = useState(0);

  const minVisibleRows = 15;

  /*
   * Altura dinámica de filas
   */
  useEffect(() => {
    const updateRowHeight = () => {
      const tableHeight = window.innerHeight * 0.8;

      setRowHeight(
        Math.floor(tableHeight / minVisibleRows)
      );
    };

    updateRowHeight();

    window.addEventListener(
      "resize",
      updateRowHeight
    );

    return () =>
      window.removeEventListener(
        "resize",
        updateRowHeight
      );
  }, []);

  /*
   * Obtener PIT
   */
  useEffect(() => {
    const fetchPit = async () => {
      try {
        const response =
          await CdrService.pit();

        setPitId(
          response.data.pitId
        );
      } catch (error) {
        if (
          error.response &&
          error.response.status === 403
        ) {
          setIsUnauthorized(true);
        }

        console.error(
          "Error fetching PIT:",
          error
        );

        setError(
          "Failed to fetch PIT."
        );
      }
    };

    fetchPit();
  }, []);

  /*
   * Obtener CDR
   *
   * Se ejecuta cuando cambia:
   *
   * - PIT
   * - filtros
   * - página
   * - tamaño
   * - columna ordenada
   * - dirección ASC/DESC
   */
  useEffect(() => {
    const fetchCdr = async () => {
      if (!pitId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response =
          await CdrService.cdr({
            pitId,

            filters,

            pagination: {
              page: pagination.page,
              size: pagination.size,
              sortBy: pagination.sortBy,
              sortOrder:
                pagination.sortOrder,
            },
          });

        setCdrData(
          response.data.data || []
        );

        setPagination((prev) => ({
          ...prev,

          total:
            response.data
              ?.pagination
              ?.total || 0,
        }));
      } catch (error) {
        console.error(
          "Error fetching CDR data:",
          error
        );

        setError(
          "Failed to fetch CDR data."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCdr();
  }, [
    pitId,
    filters,
    pagination.page,
    pagination.size,
    pagination.sortBy,
    pagination.sortOrder,
  ]);

  /*
   * Descargar CSV
   */
  const handleDownloadCsv = async () => {
    try {
      await CdrService.downloadCsv({
        pitId,
        filters,
      });
    } catch (err) {
      console.error(
        "CSV download failed:",
        err
      );

      throw new Error(
        "CSV download failed"
      );
    }
  };

  /*
   * Descargar sesiones
   */
  const handleDownloadAgentSessionsCsv =
    async () => {
      try {
        await CdrService.downloadAgentSessions({
          filters,
        });
      } catch (err) {
        console.error(
          "Agent sessions CSV download failed:",
          err
        );

        throw new Error(
          "Agent sessions CSV download failed"
        );
      }
    };

  /*
   * Aplicar filtros.
   *
   * Cada vez que cambia un filtro,
   * volvemos a página 1.
   */
  const applyFilters = (newFilters) => {
    setFilters(newFilters);

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  /*
   * Ordenación por columnas.
   *
   * Si pulsamos sobre la misma columna:
   *
   * asc -> desc
   * desc -> asc
   *
   * Si pulsamos otra columna:
   * empieza en asc.
   */
  const handleSort = (field) => {
    setPagination((prev) => {
      const sameColumn =
        prev.sortBy === field;

      const newOrder =
        sameColumn &&
        prev.sortOrder === "asc"
          ? "desc"
          : "asc";

      return {
        ...prev,
        page: 1,
        sortBy: field,
        sortOrder: newOrder,
      };
    });
  };

  return (
    <div className="h-[95vh] w-full text-text-primary-light dark:text-text-primary-dark px-4">
      {isUnauthorized ? (
        <PermissionsKo />
      ) : (
        <div className="p-4 flex flex-col gap-4 h-full">
          <CdrFilters
            setFilters={applyFilters}
            downloadCsv={
              handleDownloadCsv
            }
            downloadAgentSessionsCsv={
              handleDownloadAgentSessionsCsv
            }
          />

          <div className="flex-1 overflow-hidden">
            <div
              className="border border-gray-300 dark:border-gray-700"
              style={{
                height: "80vh",
                overflowY: "auto",
                paddingBottom: "1rem",
              }}
            >
              <CdrTable
                data={cdrData}
                loading={loading}
                error={error}
                rowHeight={rowHeight}
                visibleRows={
                  minVisibleRows
                }
                totalRows={
                  pagination.size
                }

                /*
                 * NUEVO:
                 * datos para ordenar
                 */
                sortBy={
                  pagination.sortBy
                }
                sortOrder={
                  pagination.sortOrder
                }
                onSort={handleSort}
              />
            </div>
          </div>

          <CdrPagination
            pagination={
              pagination
            }
            setPagination={
              setPagination
            }
          />
        </div>
      )}
    </div>
  );
};

export default CdrMain;